import { useState } from 'react'

import {
  USERS_ACCESS_ROLE_LABEL_KEYS,
  USERS_ACCESS_STATE_LABEL_KEYS,
} from '../../constants/usersAccess'
import { useUsersAccessMutations } from '../../hooks/useUsersAccessMutations'
import type {
  UsersAccessPendingInviteRowI,
  UsersAccessRow,
  UsersAccessTechnicianRowI,
  UsersAccessUserRowI,
} from '../../types/usersAccess'
import { canManageUsersAccessRow } from '../../utils/usersAccess'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TablePagination,
  TableRow,
  Tag,
  Text,
  Tooltip,
  UserAvatar,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { SettingsStateMessage } from '@/features/settings/dark/components/SettingsStateMessage'
import { SETTINGS_TABLE_PAGE_SIZE_OPTIONS } from '@/features/settings/dark/constants/settingsTable'
import { getSettingsTableHeight } from '@/features/settings/dark/utils/getSettingsTableHeight'
import { formatDate } from '@/utils/formatDateTime'

import { Role, RoleExtended } from '../../types/types'
import { ChangeUserRolesModal } from './ChangeUserRolesModal'
import {
  type ConfirmUsersAccessAction,
  ConfirmUsersAccessActionModal,
} from './ConfirmUsersAccessActionModal'
import { EditUsersAccessTechnicianModal } from './EditUsersAccessTechnicianModal'
import { EditUsersAccessUserModal } from './EditUsersAccessUserModal'
import { UsersAccessRowMenu } from './UsersAccessRowMenu'

const COL = {
  name: { minWidth: '260px', flexGrow: 3 },
  roles: { minWidth: '300px', flexGrow: 3 },
  state: { minWidth: '160px', flexGrow: 2 },
  activity: { minWidth: '200px', flexGrow: 2 },
  action: { minWidth: '56px', maxWidth: '56px' },
} as const

const TABLE_ROW_HEIGHT = 64

type ActiveModal =
  | { type: 'change-roles'; row: UsersAccessUserRowI }
  | { type: 'edit-user'; row: UsersAccessUserRowI }
  | { type: 'edit-technician'; row: UsersAccessTechnicianRowI }
  | { type: 'confirm'; action: ConfirmUsersAccessAction }
  | null

const RolesOverflowTag = ({
  colorScheme,
  labels,
}: {
  colorScheme: ColorScheme
  labels: string[]
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const label = labels.join(', ')

  return (
    <Box
      aria-label={label}
      as='span'
      onMouseEnter={(event) => setAnchorEl(event.currentTarget)}
      onMouseLeave={() => setAnchorEl(null)}
    >
      <Tag colorScheme={colorScheme} value={`+${labels.length}`} />
      <Tooltip
        anchorEl={anchorEl}
        colorScheme={colorScheme}
        label={label}
        placement='top'
      />
    </Box>
  )
}

interface UsersAccessTableIProps {
  colorScheme: ColorScheme
  hasActiveFilters: boolean
  isError: boolean
  isLoading: boolean
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  onRetry: () => void
  pageCount: number
  pageIndex: number
  pageSize: number
  rows: UsersAccessRow[]
  total: number
}

export const UsersAccessTable = ({
  colorScheme,
  hasActiveFilters,
  isError,
  isLoading,
  onPageChange,
  onPageSizeChange,
  onRetry,
  pageCount,
  pageIndex,
  pageSize,
  rows,
  total,
}: UsersAccessTableIProps) => {
  const { t } = useTranslation()
  const { hasCRUDPermission } = usePermissions()
  const { isResending, resendInvite } = useUsersAccessMutations(colorScheme)
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [activeModal, setActiveModal] = useState<ActiveModal>(null)

  const handleResendInvite = async (row: UsersAccessPendingInviteRowI) => {
    setResendingId(row.id)
    await resendInvite(row)
    setResendingId(null)
  }

  const renderRoles = (row: UsersAccessRow) => {
    const visibleRoles = row.roles.slice(0, 2)
    const overflowRoles = row.roles.slice(visibleRoles.length)
    return (
      <Flex alignItems='center' gap='4px' minW='0'>
        {visibleRoles.map((role) => (
          <Tag
            colorScheme={colorScheme}
            key={role}
            value={t(USERS_ACCESS_ROLE_LABEL_KEYS[role])}
          />
        ))}
        {overflowRoles.length > 0 && (
          <RolesOverflowTag
            colorScheme={colorScheme}
            labels={overflowRoles.map((role) =>
              t(USERS_ACCESS_ROLE_LABEL_KEYS[role])
            )}
          />
        )}
      </Flex>
    )
  }

  const renderActivity = (row: UsersAccessRow) => {
    if (row.kind !== 'pending-invite') {
      return <Text textStyle='body'>—</Text>
    }

    // Pending invitations intentionally reuse the legacy activity labels;
    // their meaning and copy are shared by both account-management surfaces.
    const lastResent = row.lastInviteSentAt
      ? formatDate(row.lastInviteSentAt)
      : t('settings:pending_invitees_last_resent_never')

    return (
      <Stack gap='2px'>
        <Text textStyle='caption'>
          {t('settings:pending_invitees_invited_label')}:{' '}
          {formatDate(row.invitedAt)}
        </Text>
        <Text textStyle='caption'>
          {t('settings:pending_invitees_last_resent_label')}: {lastResent}
        </Text>
      </Stack>
    )
  }

  const renderBody = () => {
    if (isError) {
      return (
        <SettingsStateMessage
          actionLabel={t('common:retry')}
          body={t('settings:rebrand_users_load_error_body')}
          colorScheme={colorScheme}
          gap='12px'
          height='100%'
          onAction={onRetry}
          title={t('settings:rebrand_users_load_error_title')}
          variant='table'
        />
      )
    }

    if (!isLoading && rows.length === 0) {
      return (
        <SettingsStateMessage
          body={t(
            hasActiveFilters
              ? 'settings:rebrand_users_no_results_body'
              : 'settings:rebrand_users_empty_body'
          )}
          colorScheme={colorScheme}
          gap='4px'
          height='100%'
          title={t(
            hasActiveFilters
              ? 'settings:rebrand_users_no_results_title'
              : 'settings:rebrand_users_empty_title'
          )}
          variant='table'
        />
      )
    }

    return rows.map((row, index) => (
      <TableRow isAlternate={index % 2 === 1} key={row.key}>
        <TableCell {...COL.name}>
          <Flex alignItems='center' gap='12px' minW='0' w='full'>
            <UserAvatar
              colorScheme={colorScheme}
              name={row.fullName || null}
              size='sm'
            />
            <Stack gap='2px' minW='0'>
              <Text
                overflow='hidden'
                textOverflow='ellipsis'
                textStyle='label'
                whiteSpace='nowrap'
              >
                {row.fullName || t('settings:rebrand_users_unknown_user')}
              </Text>
              <Text
                overflow='hidden'
                textOverflow='ellipsis'
                textStyle='caption'
                whiteSpace='nowrap'
              >
                {row.email}
              </Text>
            </Stack>
          </Flex>
        </TableCell>
        <TableCell {...COL.roles}>{renderRoles(row)}</TableCell>
        <TableCell {...COL.state}>
          <Text textStyle='body'>
            {row.accountState
              ? t(USERS_ACCESS_STATE_LABEL_KEYS[row.accountState])
              : '—'}
          </Text>
        </TableCell>
        <TableCell {...COL.activity}>{renderActivity(row)}</TableCell>
        <TableCell {...COL.action} align='center'>
          <UsersAccessRowMenu
            canManage={canManageUsersAccessRow(row, (role) =>
              hasCRUDPermission(role as Role | RoleExtended)
            )}
            colorScheme={colorScheme}
            isResending={isResending && resendingId === row.id}
            onChangeRoles={(user) =>
              setActiveModal({ type: 'change-roles', row: user })
            }
            onConfirm={(action) => setActiveModal({ type: 'confirm', action })}
            onEditTechnician={(technician) =>
              setActiveModal({ type: 'edit-technician', row: technician })
            }
            onEditUser={(user) =>
              setActiveModal({ type: 'edit-user', row: user })
            }
            onResendInvite={(invite) => void handleResendInvite(invite)}
            row={row}
          />
        </TableCell>
      </TableRow>
    ))
  }

  const tableHeight = getSettingsTableHeight({
    isLoading: isLoading && !isError,
    pageSize,
    rowCount: rows.length,
    rowHeight: TABLE_ROW_HEIGHT,
  })

  return (
    <>
      <Table
        colorScheme={colorScheme}
        density='comfortable'
        height={tableHeight}
      >
        <TableHeader>
          <TableHeaderCell {...COL.name}>
            {t('settings:rebrand_users_column_name')}
          </TableHeaderCell>
          <TableHeaderCell {...COL.roles}>
            {t('settings:rebrand_users_column_roles')}
          </TableHeaderCell>
          <TableHeaderCell {...COL.state}>
            {t('settings:rebrand_users_column_account_state')}
          </TableHeaderCell>
          <TableHeaderCell {...COL.activity}>
            {t('settings:rebrand_users_column_activity')}
          </TableHeaderCell>
          <TableHeaderCell {...COL.action} />
        </TableHeader>
        <TableBody
          isLoading={isLoading && !isError}
          skeletonRowCount={pageSize}
        >
          {renderBody()}
        </TableBody>
        <TablePagination
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageCount={Math.max(pageCount, 1)}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageSizeLabel={t('settings:rebrand_users_per_page')}
          pageSizeOptions={SETTINGS_TABLE_PAGE_SIZE_OPTIONS}
          total={total}
        />
      </Table>

      {activeModal?.type === 'change-roles' && (
        <ChangeUserRolesModal
          colorScheme={colorScheme}
          isOpen
          onClose={() => setActiveModal(null)}
          row={activeModal.row}
        />
      )}
      {activeModal?.type === 'edit-user' && (
        <EditUsersAccessUserModal
          colorScheme={colorScheme}
          isOpen
          onClose={() => setActiveModal(null)}
          row={activeModal.row}
        />
      )}
      {activeModal?.type === 'edit-technician' && (
        <EditUsersAccessTechnicianModal
          colorScheme={colorScheme}
          isOpen
          onClose={() => setActiveModal(null)}
          row={activeModal.row}
        />
      )}
      {activeModal?.type === 'confirm' && (
        <ConfirmUsersAccessActionModal
          action={activeModal.action}
          colorScheme={colorScheme}
          isOpen
          onClose={() => setActiveModal(null)}
        />
      )}
    </>
  )
}
